import { Col, H1, Row } from '@ahmdigital/ui';
import { find, isUndefined, map, range } from 'lodash/fp';
import { useRouter } from 'next/router';
import { useState } from 'react';
import cookie from 'cookie';
import webViewUtils from '@ahmdigital/logic/lib/utils/web-view';

import { FullHeightRow } from './styles';
import addDecision from './utils/add-decision';
import Branch from '../branch';
import browserUtil from '../../../../utils/browser';
import CurrentMember, { CurrentMemberOptionIDs } from './options/current-member';
import decisionTree from './data';
import getBranch from './utils/get-branch';
import LookingToJoin from './options/looking-to-join';
import OtherInsurance from './options/other-insurance';

const getDecisionBranch = getBranch(decisionTree);

export type OptionIDs =
  | CurrentMemberOptionIDs
  | 'lookingToJoin'
  | 'otherInsurance'
  | 'healthInsurance'
  | 'currentMember'
  | 'root';

const getBranchComponentHandler: Record<OptionIDs, JSX.Element | null> = {
  changeCover: <CurrentMember id="changeCover" />,
  currentMember: null,
  extrasAndClaiming: <CurrentMember id="extrasAndClaiming" />,
  healthInsurance: null,
  hospital: <CurrentMember id="hospital" />,
  loginHelp: <CurrentMember id="loginHelp" />,
  lookingToJoin: <LookingToJoin />,
  myAccount: <CurrentMember id="myAccount" />,
  otherInsurance: <OtherInsurance />,
  root: null,
  somethingElse: <CurrentMember id="somethingElse" />,
};

const DecisionTree = () => {
  const { query } = useRouter();
  const { auth: getLoginHelp, join, mfa } = query;

  const [decisions, setDecisions] = useState<ReadonlyArray<OptionIDs>>([]);
  const isMfaRedirect = !isUndefined(mfa);
  const isLookingToJoinRedirect = !isUndefined(join);
  const isLoginHelpRedirect = !isUndefined(getLoginHelp);
  const isContactUsRedirect = isMfaRedirect || isLookingToJoinRedirect || isLoginHelpRedirect;

  const scrollToElement = (id: string) => {
    const element = document.getElementById(id);
    const scrollTopOffset = 100;

    if (element) {
      element.focus({ preventScroll: true });
      window.scroll({
        behavior: 'smooth',
        top: element.getBoundingClientRect().top + window.scrollY - scrollTopOffset,
      });
    }
  };

  // @ts-expect-error - Automatic, Please fix when editing this file
  const onSelectHandler = (depth: number) => (selected: OptionIDs, content) => {
    setDecisions((prevState) => addDecision(prevState, depth, selected));

    setTimeout(() => {
      if (content?.scrollTo) {
        scrollToElement(content?.scrollTo);
      }
    }, 250);
  };

  const selectedDecision = decisions[decisions.length - 1];
  const doc = browserUtil.getDocument();
  const isWebView = webViewUtils.isWebView(cookie.parse(doc?.cookie ?? ''));
  const WrapperComponent = isWebView ? FullHeightRow : Row;

  return (
    <>
      <WrapperComponent isBoxed>
        {isMfaRedirect && (
          <Col df>
            <CurrentMember id="myAccount" showOnlyContactOptions />
          </Col>
        )}
        {isLookingToJoinRedirect && (
          <Col df>
            <LookingToJoin showOnlyContactOptions />
          </Col>
        )}
        {isLoginHelpRedirect && (
          <Col df>
            <CurrentMember id="loginHelp" showOnlyContactOptions />
          </Col>
        )}
        {!isContactUsRedirect && (
          <Col df variant="paddingBottomSpacer2">
            <H1 variant={['center', 'marginTopSpacer5', 'marginBottomSpacer11']}>Contact us</H1>
            {map(
              (index) => {
                const branch = getDecisionBranch(decisions, index);
                const selectedBranch = find({ id: decisions[index] }, branch?.branches);

                return (
                  <Branch
                    // @ts-expect-error - Automatic, Please fix when editing this file
                    key={branch.content.title}
                    // @ts-expect-error - Automatic, Please fix when editing this file
                    branches={branch.branches}
                    // @ts-expect-error - Automatic, Please fix when editing this file
                    branchHeading={branch.content.branchHeading}
                    selected={selectedBranch}
                    onSelect={onSelectHandler(index)}
                  />
                );
              },
              range(0, decisions.length + 1),
            )}
          </Col>
        )}
      </WrapperComponent>
      {selectedDecision && getBranchComponentHandler[selectedDecision]}
    </>
  );
};

export default DecisionTree;
